import { isEmpty } from 'lodash';
import { get } from 'lodash/fp';
import { ELASTIC_MAX_PAGES } from '@/utils/constants';
import { snakeCaseToTitle } from '@/utils/helpers';

export const parseKeywords = (data: any) => {
  if (!data) {
    return '';
  }
  const response = Array.isArray(data) ? data : [data];
  return response.length > 0 ? ` OR ${response.join(' OR ')}` : '';
};

export const parsePagination = (data: any) => {
  return {
    currentPage: data.current,
    resultsPerPage: data.size,
    totalResults: data.total_results,
    totalPages: data.total_pages < ELASTIC_MAX_PAGES ? data.total_pages : ELASTIC_MAX_PAGES,
    from: data.size * (data.current - 1) + 1,
    to: Math.min(data.total_results, data.current * data.size),
  };
};

export const parseSortByDefault = (sortBy: string, sortType?: string) => {
  if (sortType) {
    return {
      [sortBy]: sortType,
    };
  }

  if (/most-recent/.test(sortBy)) {
    return [{ release_date: 'desc' }, { modification_date: 'desc' }];
  }

  if (/title-desc/.test(sortBy)) {
    return { title: 'desc' };
  }

  if (/title-asc/.test(sortBy)) {
    return { title: 'asc' };
  }

  return { _score: 'desc' };
};

export const parseSortByPublicationDate = (sortBy: string, sortType?: string) => {
  if (sortType) {
    return {
      [sortBy]: sortType,
    };
  }

  if (/most-recent/.test(sortBy)) {
    return [{ release_date: 'desc' }, { publication_date: 'desc' }, { modification_date: 'desc' }];
  }

  if (/title-desc/.test(sortBy)) {
    return { title: 'desc' };
  }

  if (/title-asc/.test(sortBy)) {
    return { title: 'asc' };
  }

  return { _score: 'desc' };
};

export const mapFacetsToElastic = (facets: string[]) =>
  facets.reduce(
    (obj, facet) => ({
      ...obj,
      [facet]: [
        {
          type: 'value',
          size: 250,
        },
      ],
    }),
    {},
  );

export const mapFacetsElasticToReact = (facets: string[], data: any = {}) =>
  facets.reduce(
    (obj, facet): any => ({
      ...obj,
      [facet]: data?.[facet]?.[0]?.data,
    }),
    {},
  );

export const newMapFacetsElasticToReact = (facets: string[], data: any, labels: any = {}) =>
  facets
    .filter((facet) => data[facet]?.[0]?.data?.length)
    .reduce((obj: any[], facet) => {
      const keyLabel = facet.split('_').join('-');
      const label = labels?.[keyLabel] || snakeCaseToTitle(facet);

      return [
        ...obj,
        {
          name: facet,
          label,
          options: data[facet][0].data.map((option: any) => ({
            value: option.value,
            count: null,
          })),
        },
      ];
    }, []);

export const mapResultFieldsToElastic = (fields: any) =>
  (Array.isArray(fields) ? fields : Object.keys(fields)).reduce(
    (obj: any, item: any) => ({
      ...obj,
      [item]: {
        raw: {},
      },
    }),
    {},
  );

export const removeEmpty = ({
  filters,
  type = 'all',
  onlyPublished,
  filtersFallbackMap,
}: {
  filters: any;
  type: 'all' | 'any' | 'none';
  onlyPublished: boolean;
  filtersFallbackMap: Map<string, string[]>;
}) => {
  const initialValue = onlyPublished ? [{ published: 'true' }] : []; // Filter by published = true
  return {
    [type]: Object.keys(filters)
      // Do NOT use "isEmpty" in the next line, we need to pass empty arrays if needed (that !! doesn't verify empty arrays).
      .filter((key) => {
        return !!filters[key];
      })
      .reduce((obj: any[], key: string) => {
        if (filters[key].length) {
          return [...obj, { [key]: filters[key] }];
        }

        if (filtersFallbackMap.has(key) && !isEmpty(filtersFallbackMap.get(key))) {
          return [...obj, { [key]: filtersFallbackMap.get(key) }];
        }

        return obj;
      }, initialValue),
  };
};

export const setFacetNumbers = (
  facets: any,
  facetsFilled: any,
  selectedFilters: any,
  hideFilters: string[] = [],
  labels: any = {},
) => {
  return facets
    .filter((facet: any) => !hideFilters.includes(facet.name))
    .map((facet: any) => ({
      ...facet,
      options: facet.options.map((opt: any) => ({ ...opt, count: null })),
    }))
    .map((it: any) => {
      if (facetsFilled?.[it.name]) {
        const keyLabel = it.name?.replace(/^tax_/, '')?.replace(/_/g, '-');
        const label = labels?.[keyLabel] || snakeCaseToTitle(it.name);

        return {
          name: it.name,
          label,
          options: it.options.map((opt: any) => ({
            ...(facetsFilled[it.name].find((val: any) => val.value === opt.value) || opt),
            label: labels?.[opt.value] || opt.value || '',
            selected: selectedFilters[it.name]
              ? selectedFilters[it.name].includes(opt.value)
              : false,
          })),
        };
      }
      return it;
    });
};

export const PROTECTED_CHARACTER = '-';
export const wrapQuery = (searchTerm: string) =>
  searchTerm
    .split(' ')
    .map((text) => (text.includes(PROTECTED_CHARACTER) ? `+${text}` : text))
    .join(' ');

/*
 * For converting tax_product_line to product-line/
 * it's used to filter the keys based on the facet
 */
const facetToTaxonomyKey = (facet: string) =>
  facet.replace(/^tax_/, '').replace(/_/g, '-').concat('/');

/*
 * Given a key list and facet, it matches filter the keys based on the facet,
 * to match them to the taxonomy value
 */
const mapKeysToFilters = ({
  keys,
  taxonomy,
  facet = '',
}: {
  keys: string[];
  taxonomy: any;
  facet: string;
}): string[] =>
  keys?.length
    ? keys
        .filter((key: string) => key.startsWith(facetToTaxonomyKey(facet)))
        .map((key: string) => get(key, taxonomy))
    : [];

/*
 * it parses a key list to facet filter structure
 */
export const parseKeysToFilters = (facetsList: string[], taxonomy: any, keys: string[] = []) =>
  facetsList?.reduce(
    (prev = {}, facet: string) => ({
      ...prev,
      [facet]: mapKeysToFilters({ keys, taxonomy, facet }),
    }),
    {},
  );
